import { AxiosResponse } from "axios";
import { put, call, throttle, takeLatest, select } from "redux-saga/effects";
// NOTE: Do NOT import decryptJson — loyalty APIs return plain JSON
import {
  successLoyaltyEnroll, errorLoyaltyEnroll,
  successLoyaltyLookup, errorLoyaltyLookup,
  successLoyaltyDashboard, errorLoyaltyDashboard,
  successLoyaltyProgramExists, errorLoyaltyProgramExists,
  requestLoyaltyAvailableRewards,
  requestLoyaltyDashboard,
  successLoyaltyAvailableRewards, errorLoyaltyAvailableRewards,
  successLoyaltyApplyReward, errorLoyaltyApplyReward,
  setLoyaltyAppliedRedemptions,
  successLoyaltyRemoveReward, errorLoyaltyRemoveReward,
  successLoyaltyPointsPreview, errorLoyaltyPointsPreview,
  successLoyaltyOrderIdentify, errorLoyaltyOrderIdentify,
  successLoyaltyCheckoutSummary, errorLoyaltyCheckoutSummary,
  successLoyaltyOrderComplete, errorLoyaltyOrderComplete,
  successLoyaltyAccruePoints, errorLoyaltyAccruePoints,
  successLoyaltyActivePromos,
  successLoyaltyReceiptData, errorLoyaltyReceiptData,
  successLoyaltyGenerateWalletPass, errorLoyaltyGenerateWalletPass,
  successLoyaltyGetWalletPass, errorLoyaltyGetWalletPass,
  successLoyaltyCustomerTransactions, errorLoyaltyCustomerTransactions,
} from "./loyaltyActions";
import {
  loyaltyLookupAPI, loyaltyEnrollAPI, loyaltyDashboardAPI,
  loyaltyAvailableRewardsAPI,
  loyaltyApplyRewardAPI, loyaltyRemoveRewardAPI,
  loyaltyPointsPreviewAPI, loyaltyOrderIdentifyAPI,
  loyaltyCheckoutSummaryAPI, loyaltyOrderCompleteAPI,
  loyaltyAccruePointsAPI,
  loyaltyReceiptDataAPI,
  loyaltyGenerateWalletPassAPI, loyaltyGetWalletPassAPI,
  loyaltyCustomerTransactionsAPI,
} from "./loyaltyAPI";
import {
  LOYALTY_ENROLL_REQUEST, LOYALTY_LOOKUP_REQUEST,
  LOYALTY_DASHBOARD_REQUEST,
  LOYALTY_PROGRAM_EXISTS_REQUEST,
  LOYALTY_AVAILABLE_REWARDS_REQUEST, LOYALTY_APPLY_REWARD_REQUEST,
  LOYALTY_REMOVE_REWARD_REQUEST, LOYALTY_POINTS_PREVIEW_REQUEST,
  LOYALTY_ORDER_IDENTIFY_REQUEST, LOYALTY_CHECKOUT_SUMMARY_REQUEST,
  LOYALTY_ORDER_COMPLETE_REQUEST,
  LOYALTY_RECEIPT_DATA_REQUEST, LOYALTY_WALLET_PASS_GENERATE_REQUEST,
  LOYALTY_WALLET_PASS_GET_REQUEST,
  LOYALTY_ACCRUE_POINTS_REQUEST,
  LOYALTY_CUSTOMER_TRANSACTIONS_REQUEST,
} from "./loyaltyConstants";
import {
  LoyaltyEnrollRequest, LoyaltyApplyRewardRequest,
  LoyaltyPointsPreviewRequest, LoyaltyOrderIdentifyRequest,
  LoyaltyOrderCompleteRequest, LoyaltyAccruePointsRequest,
} from "./loyaltyModels";

const getLocationId = (state: any) => state.restaurant?.currentRestaurantDetail?.id || '';

console.log("LOYALTY BASE URL:", process.env.REACT_APP_API_ENDPOINT_LOYALTY);

function* enrollSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const response: AxiosResponse = yield call(loyaltyEnrollAPI, action.payload as LoyaltyEnrollRequest, brandId);
    if (response.status === 201 || response.status === 200) {
      yield put(successLoyaltyEnroll(response.data?.data || response.data));
    } else {
      yield put(errorLoyaltyEnroll());
    }
  } catch (err) { yield put(errorLoyaltyEnroll()); }
}

function* programExistsSaga(_action: any) {
  try {
    const isLoyaltyProgramEnabled: boolean = yield select(
      (state: any) => Boolean(state.restaurant?.currentRestaurantDetail?.isLoyaltyProgramEnabled)
    );
    yield put(successLoyaltyProgramExists({
      brand_has_program: isLoyaltyProgramEnabled,
      location_has_program: isLoyaltyProgramEnabled,
      program_status: isLoyaltyProgramEnabled ? "active" : null,
    }));
  } catch (err) {
    yield put(errorLoyaltyProgramExists());
  }
}

function* lookupSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const { phone, locationId } = action.payload as { phone: string; locationId: string };
    const response: AxiosResponse = yield call(loyaltyLookupAPI, phone, locationId, brandId);
    if (response.status === 200) {
      // Backend (pos.use-case.ts lookupByPhone) returns one of:
      //   not-enrolled: { data: { found: false, member: null } }
      //   enrolled:     { data: { found: true,  member: { id, current_tier, available_rewards, ... } } }
      // Reshape the enrolled case into the flat LoyaltyLookupResult the redux model + downstream
      // consumers expect — `loyalty_id` is the canonical customer-id field they read.
      const raw = response.data?.data || response.data || {};
      const member = raw.member;
      if (raw.found === false || !member) {
        // Treat "not enrolled" as a lookup error so isEnrolled stays false and the dashboard
        // effect's guard short-circuits.
        yield put(errorLoyaltyLookup());
        return;
      }
      const reshaped: any = {
        ...member,
        loyalty_id: member.id || member.loyalty_id || '',
        customer_id: member.customer_id || '',
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        current_tier: member.current_tier || null,
        points_balance: member.points_balance ?? 0,
        available_rewards: member.available_rewards || [],
      };
      yield put(successLoyaltyLookup(reshaped));
    } else {
      yield put(errorLoyaltyLookup());
    }
  } catch (err) { yield put(errorLoyaltyLookup()); }
}

function* dashboardSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const { loyaltyCustomerId, locationId } = action.payload as { loyaltyCustomerId: string; locationId: string };
    const response: AxiosResponse = yield call(loyaltyDashboardAPI, loyaltyCustomerId, locationId, brandId);
    if (response.status === 200) {
      const raw = response.data?.data || response.data;
      // Map recent_activity (raw DB fields) to recent_transactions
      // Backend may return under various keys depending on version
      const rawTxns = raw?.recent_activity || raw?.recent_transactions
        || raw?.transactions || raw?.activity || raw?.points_history
        || raw?.transaction_history || [];
      const recent_transactions = rawTxns.map((t: any) => ({
        transaction_id: t.transaction_id || t.id,
        type: t.type || t.transaction_type,
        points: t.points,
        description: t.description || t.adjustment_reason || t.transaction_type || '',
        location_name: t.location_name || null,
        created_at: t.created_at,
      }));

      // Reshape backend response to nested frontend LoyaltyDashboard structure
      // Backend returns tier data as: data.tier.current_tier / data.tier.next_tier
      // OR flat: data.current_tier
      const tierData = raw.tier || {};
      let currentTier = tierData.current_tier || raw.current_tier;
      const backendNextTier = tierData.next_tier || raw.next_tier || null;

      const stats = raw.lifetime_stats || {};
      const customerData = raw.customer || {};
      const dashboard: any = {
        customer: {
          loyalty_customer_id: raw.loyalty_customer_id || customerData.loyalty_customer_id || '',
          customer_id: raw.customer_id || customerData.customer_id || '',
          first_name: raw.first_name || customerData.first_name || '',
          last_name: raw.last_name || customerData.last_name || '',
          email: raw.email || customerData.email || '',
          phone: raw.phone || customerData.phone || '',
          enrolled_at: raw.enrolled_at || customerData.enrolled_at || '',
          total_visits: stats.total_visits ?? customerData.total_visits ?? 0,
          lifetime_points_earned: stats.points_earned ?? customerData.lifetime_points_earned ?? 0,
        },
        points: {
          current_balance: raw.points_balance ?? 0,
          pending_points: 0,
          points_expiring_soon: null,
        },
        tier: {
          current_tier: currentTier ? {
            tier_id: currentTier.tier_id || '',
            name: currentTier.name || '',
            rank: currentTier.rank ?? undefined,
            description: currentTier.description || '',
            color_hex: currentTier.color_hex || '',
            icon_url: currentTier.icon || currentTier.icon_url || '',
            multiplier: currentTier.points_multiplier ?? currentTier.multiplier ?? 1,
            assigned_at: currentTier.assigned_at ?? undefined,
            change_type: currentTier.change_type ?? undefined,
            benefits: currentTier.benefits || [],
            promotions: currentTier.promotions || [],
          } : null,
          next_tier: backendNextTier ? {
            tier_id: backendNextTier.tier_id || '',
            name: backendNextTier.name || '',
            color_hex: backendNextTier.color_hex || '',
            icon_url: backendNextTier.icon || backendNextTier.icon_url || '',
            description: backendNextTier.description || '',
            qualification_type: backendNextTier.qualification_type || '',
            points_required: backendNextTier.points_required ?? 0,
            points_remaining: backendNextTier.points_remaining ?? 0,
            current_value: backendNextTier.current_value ?? 0,
            progress_percentage: backendNextTier.progress_percentage ?? 0,
            benefits: backendNextTier.benefits || [],
            promotions: backendNextTier.promotions || [],
          } : null,
          tier_locked_until: null,
        },
        available_rewards: raw.available_rewards || [],
        active_promotions: [],
        recent_transactions,
        wallet_pass: null,
      };

      // Map active_promotions from dashboard response
      const mappedActivePromos = (raw.active_promotions || []).map((p: any) => ({
        promotion_id: p.promotion_id || p.id,
        name: p.name || '',
        description: p.description || '',
        type: p.type || p.promo_type || '',
        value: p.value ?? (
          p.promo_type === 'points_multiplier' ? Number(p.multiplier_value || 0)
          : p.promo_type === 'bonus_points' ? Number(p.flat_bonus_points || 0)
          : Number(p.boost_multiplier || p.boost_flat_points || 0)
        ),
        starts_at: p.starts_at || p.start_date || '',
        ends_at: p.ends_at || p.end_date || '',
        image_url: p.image_url || null,
      }));

      // Map available_rewards to set can_redeem based on points balance
      const pointsBalance = raw.points_balance ?? 0;
      const rawRewards = raw.available_rewards || [];
      dashboard.available_rewards = rawRewards.map((r: any) => ({
        reward_id: r.reward_id || r.id,
        name: r.name || '',
        description: r.description || '',
        reward_type: r.reward_type || '',
        points_cost: r.points_cost ?? 0,
        discount_value: r.dollar_off_amount ?? r.percentage_off_value ?? r.discount_value ?? null,
        image_url: r.image_url || null,
        can_redeem: r.can_redeem !== undefined ? r.can_redeem : pointsBalance >= (r.points_cost ?? 0),
        combinable_with_promotions: r.redemption_rules?.combinable_with_promotions ?? r.combinable_with_promotions ?? true,
        usage_limit_type: r.redemption_rules?.usage_limit_type ?? r.usage_limit_type ?? 'unlimited',
        tier_name: r.tier_name ?? null,
        reason: r.reason || undefined,
        // Fields the backend returns but does not enforce at redeem time — carried through
        // so the client can gate Apply before calling the API.
        min_order_subtotal: r.min_order_subtotal != null ? Number(r.min_order_subtotal) : 0,
        valid_from: r.valid_from ?? null,
        valid_till: r.valid_till ?? null,
        order_type: Array.isArray(r.order_type)
          ? { channels: r.order_type }
          : r.order_type?.channels
            ? { channels: r.order_type.channels }
            : null,
        tier_id: r.tier_id ?? null,
        is_combinable: r.is_combinable ?? true,
        automated: r.automated ?? false,
        automated_type: r.automated_type ?? null,
        dollar_off_amount: r.dollar_off_amount ?? null,
        percentage_off_value: r.percentage_off_value ?? null,
        percentage_off_cap: r.percentage_off_cap ?? null,
        value_per_points: r.value_per_points ?? null,
        display_name: r.display_name ?? null,
      }));

      yield put(successLoyaltyDashboard(dashboard));

      yield put(successLoyaltyActivePromos(mappedActivePromos));
    } else {
      yield put(errorLoyaltyDashboard());
    }
  } catch (err) { yield put(errorLoyaltyDashboard()); }
}


function* availableRewardsSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const { loyaltyCustomerId, locationId, orderTotal, orderId } = action.payload as any;
    const response: AxiosResponse = yield call(loyaltyAvailableRewardsAPI, loyaltyCustomerId, locationId, brandId, "online", orderTotal, orderId);
    if (response.status === 200) {
      const raw = response.data?.data || response.data;
      const pointsBalance = raw.points_balance ?? 0;
      // Backend returns eligible_rewards with raw DB fields; reshape to frontend LoyaltyRewardItem
      const rawRewards = raw.eligible_rewards || raw.available_rewards || [];
      const available_rewards = rawRewards.map((r: any) => ({
        reward_id: r.reward_id || r.id,
        name: r.name || '',
        description: r.description || '',
        reward_type: r.reward_type || '',
        points_cost: r.points_cost ?? 0,
        discount_value: r.dollar_off_amount ?? r.percentage_off_value ?? r.discount_value ?? null,
        image_url: r.image_url || null,
        can_redeem: r.can_redeem !== undefined ? r.can_redeem : pointsBalance >= (r.points_cost ?? 0),
        combinable_with_promotions: r.redemption_rules?.combinable_with_promotions ?? r.combinable_with_promotions ?? true,
        usage_limit_type: r.redemption_rules?.usage_limit_type ?? r.usage_limit_type ?? 'unlimited',
        tier_name: r.tier_name ?? null,
        reason: r.reason || undefined,
        // Source of truth for "is this reward already on the current order" — set when order_id was passed.
        is_redeemed: r.is_redeemed === true,
        redemption_id: r.redemption_id || undefined,
        // Fields the backend returns but does not enforce at redeem time — carried through
        // so the client can gate Apply before calling the API.
        min_order_subtotal: r.min_order_subtotal != null ? Number(r.min_order_subtotal) : 0,
        valid_from: r.valid_from ?? null,
        valid_till: r.valid_till ?? null,
        order_type: Array.isArray(r.order_type)
          ? { channels: r.order_type }
          : r.order_type?.channels
            ? { channels: r.order_type.channels }
            : null,
        tier_id: r.tier_id ?? null,
        is_combinable: r.is_combinable ?? true,
        automated: r.automated ?? false,
        automated_type: r.automated_type ?? null,
        dollar_off_amount: r.dollar_off_amount ?? null,
        percentage_off_value: r.percentage_off_value ?? null,
        percentage_off_cap: r.percentage_off_cap ?? null,
        value_per_points: r.value_per_points ?? null,
        display_name: r.display_name ?? null,
        display_points: r.display_points ?? null,
      }));
      yield put(successLoyaltyAvailableRewards({
        loyalty_customer_id: raw.loyalty_customer_id || loyaltyCustomerId,
        points_balance: pointsBalance,
        available_rewards,
      } as any));
    } else {
      yield put(errorLoyaltyAvailableRewards());
    }
  } catch (err) { yield put(errorLoyaltyAvailableRewards()); }
}

function* applyRewardSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const { orderId, payload } = action.payload as { orderId: string; payload: LoyaltyApplyRewardRequest };
    const response: AxiosResponse = yield call(loyaltyApplyRewardAPI, orderId, payload, brandId);
    if (response.status === 201 || response.status === 200) {
      const raw = response.data?.data || response.data;

      // Backend response shape differs by single vs bulk:
      //   single: { redemption_id, reward_id, reward_name, reward_type, points_spent, discount_amount, status, new_balance, ... }
      //   bulk:   { redemptions: [{redemption_id, reward_id, ...}, ...], voided: [...], total_points_spent, new_balance, ... }
      // Normalise both into a single redemptions array we can iterate over.
      const isBulk = Array.isArray(raw?.redemptions);
      const normalisedRedemptions: any[] = isBulk
        ? raw.redemptions
        : [{
            redemption_id: raw.redemption_id || raw.application_id || '',
            reward_id: raw.reward_id || payload.reward_id || '',
            reward_name: raw.reward_name || '',
            reward_type: raw.reward_type || '',
            points_spent: raw.points_spent ?? raw.points_reserved ?? 0,
            discount_amount: raw.discount_value ?? raw.discount_amount ?? 0,
            status: raw.status || 'applied',
          }];

      // Pick the "primary" applied reward for the legacy single-reward redux slot.
      // For bulk, the panel watches `appliedRedemptions` instead.
      const primary = normalisedRedemptions[0] || {};
      yield put(successLoyaltyApplyReward({
        application_id: primary.redemption_id || primary.application_id || '',
        order_id: raw.order_id || orderId || '',
        reward_id: primary.reward_id || '',
        reward_name: primary.reward_name || '',
        reward_type: primary.reward_type || '',
        points_reserved: primary.points_spent ?? 0,
        discount_amount: primary.discount_amount ?? 0,
        status: primary.status || 'applied',
        points_balance_after: raw.new_balance ?? raw.points_balance_after ?? 0,
        expires_at: raw.expires_at || raw.created_at || '',
      }));

      // Store the FULL list of currently-applied redemptions so the panel can rebuild its
      // remove-button mapping after re-opening (the legacy single-slot loses bulk redeems).
      yield put(setLoyaltyAppliedRedemptions(
        normalisedRedemptions
          .filter((r: any) => r.reward_id && (r.redemption_id || r.application_id))
          .map((r: any) => ({
            reward_id: r.reward_id,
            application_id: r.redemption_id || r.application_id,
            reward_name: r.reward_name,
            reward_type: r.reward_type,
            points_spent: r.points_spent,
            discount_amount: r.discount_amount,
          }))
      ));

      // Refresh available rewards (now with order_id so the backend populates is_redeemed) and
      // the dashboard balance.
      yield put(requestLoyaltyAvailableRewards({
        loyaltyCustomerId: payload.loyalty_customer_id,
        locationId: payload.location_id,
        orderId: orderId || undefined,
      }));
      yield put(requestLoyaltyDashboard({
        loyaltyCustomerId: payload.loyalty_customer_id,
        locationId: payload.location_id,
      }));
    } else {
      const errMsg = response.data?.message || response.data?.error?.message || "Failed to apply reward";
      yield put(errorLoyaltyApplyReward(errMsg));
    }
  } catch (err: any) {
    const errData = err?.response?.data;
    const status = err?.response?.status;
    let errMsg = errData?.message || errData?.error?.message || '';
    if (!errMsg) {
      if (status === 400) errMsg = "Insufficient points to redeem this reward";
      else if (status === 404) errMsg = "Reward not found";
      else errMsg = "Failed to apply reward. Please try again.";
    }
    yield put(errorLoyaltyApplyReward(errMsg));
  }
}

function* removeRewardSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const { orderId, applicationId, loyaltyCustomerId, locationId } = action.payload as {
      orderId: string; applicationId: string; loyaltyCustomerId?: string; locationId?: string;
    };
    const response: AxiosResponse = yield call(loyaltyRemoveRewardAPI, orderId, applicationId, brandId);
    if (response.status === 200) {
      yield put(successLoyaltyRemoveReward(response.data?.data || response.data));
      // Refresh available rewards (with order_id so backend re-computes is_redeemed for the
      // remaining redemptions on this order) and the dashboard balance.
      if (loyaltyCustomerId && locationId) {
        yield put(requestLoyaltyAvailableRewards({ loyaltyCustomerId, locationId, orderId: orderId || undefined }));
        yield put(requestLoyaltyDashboard({ loyaltyCustomerId, locationId }));
      }
    } else {
      yield put(errorLoyaltyRemoveReward());
    }
  } catch (err) { yield put(errorLoyaltyRemoveReward()); }
}

function* pointsPreviewSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const { orderId, payload } = action.payload as { orderId: string; payload: LoyaltyPointsPreviewRequest };
    const response: AxiosResponse = yield call(loyaltyPointsPreviewAPI, orderId, payload, brandId);
    if (response.status === 200) {
      yield put(successLoyaltyPointsPreview(response.data?.data || response.data));
    } else {
      yield put(errorLoyaltyPointsPreview());
    }
  } catch (err) { yield put(errorLoyaltyPointsPreview()); }
}

function* orderIdentifySaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const response: AxiosResponse = yield call(loyaltyOrderIdentifyAPI, action.payload as LoyaltyOrderIdentifyRequest, brandId);
    if (response.status === 200) {
      yield put(successLoyaltyOrderIdentify(response.data?.data || response.data));
    } else {
      yield put(errorLoyaltyOrderIdentify());
    }
  } catch (err) { yield put(errorLoyaltyOrderIdentify()); }
}

function* checkoutSummarySaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const { orderId, locationId } = action.payload as { orderId: string; locationId: string };
    const response: AxiosResponse = yield call(loyaltyCheckoutSummaryAPI, orderId, locationId, brandId);
    if (response.status === 200) {
      yield put(successLoyaltyCheckoutSummary(response.data?.data || response.data));
    } else {
      yield put(errorLoyaltyCheckoutSummary());
    }
  } catch (err) { yield put(errorLoyaltyCheckoutSummary()); }
}

function* orderCompleteSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const { orderId, payload } = action.payload as { orderId: string; payload: LoyaltyOrderCompleteRequest };
    const response: AxiosResponse = yield call(loyaltyOrderCompleteAPI, orderId, payload, brandId);
    if (response.status === 200) {
      yield put(successLoyaltyOrderComplete(response.data?.data || response.data));
    } else {
      yield put(errorLoyaltyOrderComplete());
    }
  } catch (err) { yield put(errorLoyaltyOrderComplete()); }
}

function* accruePointsSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const payload = action.payload as LoyaltyAccruePointsRequest;
    const response: AxiosResponse = yield call(loyaltyAccruePointsAPI, payload, brandId);
    if (response.status === 200 || response.status === 201) {
      const raw = response.data?.data || response.data;
      yield put(successLoyaltyAccruePoints(raw));
      // Refresh dashboard to reflect new points balance
      if (payload.loyalty_customer_id && payload.location_id) {
        yield put(requestLoyaltyDashboard({
          loyaltyCustomerId: payload.loyalty_customer_id,
          locationId: payload.location_id,
        }));
      }
    } else {
      const msg = response.data?.message || response.data?.error?.message || 'Failed to accrue points';
      yield put(errorLoyaltyAccruePoints(msg));
    }
  } catch (err: any) {
    const msg = err?.response?.data?.message || err?.response?.data?.error?.message || 'Failed to accrue points';
    yield put(errorLoyaltyAccruePoints(msg));
  }
}

function* receiptDataSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const response: AxiosResponse = yield call(loyaltyReceiptDataAPI, action.payload as string, brandId);
    if (response.status === 200) {
      const receiptPayload = response.data?.data;
      if (receiptPayload) {
        yield put(successLoyaltyReceiptData(receiptPayload));
      } else {
        yield put(successLoyaltyReceiptData({ order_id: action.payload, __empty: true } as any));
      }
    } else {
      yield put(errorLoyaltyReceiptData());
    }
  } catch (err) { yield put(errorLoyaltyReceiptData()); }
}

function* generateWalletPassSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const { loyaltyCustomerId, passType, programId } = action.payload as any;
    const response: AxiosResponse = yield call(loyaltyGenerateWalletPassAPI, loyaltyCustomerId, passType, programId, brandId);
    if (response.status === 201 || response.status === 200) {
      yield put(successLoyaltyGenerateWalletPass(response.data?.data || response.data));
    } else {
      yield put(errorLoyaltyGenerateWalletPass());
    }
  } catch (err) { yield put(errorLoyaltyGenerateWalletPass()); }
}

function* getWalletPassSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const response: AxiosResponse = yield call(loyaltyGetWalletPassAPI, action.payload as string, brandId);
    if (response.status === 200) {
      yield put(successLoyaltyGetWalletPass(response.data?.data?.passes?.[0] || null));
    } else {
      yield put(errorLoyaltyGetWalletPass());
    }
  } catch (err) { yield put(errorLoyaltyGetWalletPass()); }
}

// Maps backend transaction_type values to the frontend LoyaltyTransaction type enum.
const TX_TYPE_MAP: Record<string, string> = {
  earn: 'accrual',
  accrual: 'accrual',
  redeem: 'redemption',
  redemption: 'redemption',
  expiry: 'expiry',
  expired: 'expiry',
  adjustment: 'adjustment',
  bonus: 'bonus',
  adjust_credit: 'adjust_credit',
  adjust_debit: 'adjust_debit',
};

function* customerTransactionsSaga(action: any) {
  try {
    const brandId: string = yield select(getLocationId);
    const loyaltyCustomerId = action.payload as string;
    const response: AxiosResponse = yield call(loyaltyCustomerTransactionsAPI, loyaltyCustomerId, brandId);
    if (response.status === 200) {
      const raw = response.data?.data || response.data;
      const rawTxns: any[] = raw?.transactions || [];
      const pointsName: string = raw?.points_name || 'Points';
      const transactions = rawTxns.map((t: any) => ({
        transaction_id: t.transaction_id || t.id,
        type: TX_TYPE_MAP[t.transaction_type || t.type] || t.transaction_type || t.type || 'accrual',
        points: t.points ?? 0,
        description: t.description || t.adjustment_reason || t.transaction_type || t.type || '',
        message: t.message || null,
        adjustment_reason: t.adjustment_reason || null,
        channel: t.channel || null,
        location_name: t.location_name || null,
        created_at: t.created_at,
        balance_after: t.balance_after ?? undefined,
        order_id: t.order_id ?? undefined,
      }));
      yield put(successLoyaltyCustomerTransactions({ transactions, pointsName }));
    } else {
      yield put(errorLoyaltyCustomerTransactions());
    }
  } catch (err) { yield put(errorLoyaltyCustomerTransactions()); }
}

export default function* loyaltySaga() {
  yield throttle(5000, LOYALTY_ENROLL_REQUEST, enrollSaga);
  yield throttle(5000, LOYALTY_PROGRAM_EXISTS_REQUEST, programExistsSaga);
  yield throttle(5000, LOYALTY_LOOKUP_REQUEST, lookupSaga);
  yield takeLatest(LOYALTY_DASHBOARD_REQUEST, dashboardSaga);
  yield takeLatest(LOYALTY_AVAILABLE_REWARDS_REQUEST, availableRewardsSaga);
  // Use takeLatest (not throttle) for apply-reward: the cart saga fires this from
  // dispatchLoyaltyRedeemAfterOrder right after placeOrder succeeds. A 5s throttle window
  // could drop that dispatch if the panel had fired one earlier in the session, causing
  // pay-at-store redeems to silently disappear. takeLatest guarantees the newest request
  // always runs while still cancelling any in-flight duplicate.
  yield takeLatest(LOYALTY_APPLY_REWARD_REQUEST, applyRewardSaga);
  yield throttle(5000, LOYALTY_REMOVE_REWARD_REQUEST, removeRewardSaga);
  yield throttle(5000, LOYALTY_POINTS_PREVIEW_REQUEST, pointsPreviewSaga);
  yield throttle(5000, LOYALTY_ORDER_IDENTIFY_REQUEST, orderIdentifySaga);
  yield throttle(5000, LOYALTY_CHECKOUT_SUMMARY_REQUEST, checkoutSummarySaga);
  yield throttle(5000, LOYALTY_ORDER_COMPLETE_REQUEST, orderCompleteSaga);
  yield throttle(5000, LOYALTY_ACCRUE_POINTS_REQUEST, accruePointsSaga);
  yield throttle(5000, LOYALTY_RECEIPT_DATA_REQUEST, receiptDataSaga);
  yield throttle(5000, LOYALTY_WALLET_PASS_GENERATE_REQUEST, generateWalletPassSaga);
  yield throttle(5000, LOYALTY_WALLET_PASS_GET_REQUEST, getWalletPassSaga);
  yield throttle(5000, LOYALTY_CUSTOMER_TRANSACTIONS_REQUEST, customerTransactionsSaga);
}
